import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../../../firebase/firebaseConfig";
import {
  collection,
  deleteDoc,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import {
  FiAlertTriangle,
  FiBarChart2,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiClipboard,
  FiEdit3,
  FiSearch,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";
import AssignmentProgress from "./AssignmentProgress";
import dayjs from "dayjs";

function displayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

const fmt = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "—");
const isOver = (deadline) => deadline && dayjs(deadline).endOf("day").isBefore(dayjs());

export default function AssignmentsList({ classInfo, students, onEdit }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const qRef = query(
      collection(db, "assignments"),
      where("classId", "==", classInfo.id),
      orderBy("deadline", "desc"),
      limit(100)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAssignments(arr);
        setLoading(false);
      },
      (e) => {
        console.error(e);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [classInfo.id]);

  const handleDelete = async (asg) => {
    const title = displayText(asg.title, asg.id);
    if (!window.confirm(`Xoá assignment "${title}"?`)) return;
    try {
      await deleteDoc(doc(db, "assignments", asg.id));
      toast.success("Đã xoá assignment.");
    } catch (e) {
      console.error(e);
      toast.error("Xoá thất bại.");
    }
  };

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return assignments.filter((asg) => {
      const title = displayText(asg.title, "").toLowerCase();
      const note = displayText(asg.note, "").toLowerCase();
      const okSearch = !keyword || `${title} ${note} ${asg.testId || ""}`.includes(keyword);
      const okStatus =
        statusFilter === "all" ||
        (statusFilter === "published" && asg.published) ||
        (statusFilter === "draft" && !asg.published) ||
        (statusFilter === "overdue" && isOver(asg.deadline));
      return okSearch && okStatus;
    });
  }, [assignments, search, statusFilter]);

  const stats = useMemo(
    () => ({
      total: assignments.length,
      published: assignments.filter((a) => a.published).length,
      draft: assignments.filter((a) => !a.published).length,
      overdue: assignments.filter((a) => isOver(a.deadline)).length,
    }),
    [assignments]
  );

  return (
    <div className="rounded-2xl border bg-white shadow-sm">
      <div className="border-b p-4 md:p-5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
              <FiClipboard className="text-blue-600" /> Bài tập đã giao
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Theo dõi trạng thái công khai, hạn nộp và tiến độ từng học viên.
            </p>
          </div>

          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="rounded-xl border bg-slate-50 px-3 py-2">
              <div className="font-bold text-blue-700">{stats.total}</div>
              <div className="text-[11px] text-slate-500">Tất cả</div>
            </div>
            <div className="rounded-xl border bg-slate-50 px-3 py-2">
              <div className="font-bold text-emerald-600">{stats.published}</div>
              <div className="text-[11px] text-slate-500">Published</div>
            </div>
            <div className="rounded-xl border bg-slate-50 px-3 py-2">
              <div className="font-bold text-amber-600">{stats.draft}</div>
              <div className="text-[11px] text-slate-500">Draft</div>
            </div>
            <div className="rounded-xl border bg-slate-50 px-3 py-2">
              <div className="font-bold text-red-600">{stats.overdue}</div>
              <div className="text-[11px] text-slate-500">Hết hạn</div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tiêu đề, ghi chú hoặc Test ID..."
              className="w-full rounded-xl border bg-white py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="all">Tất cả trạng thái</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="overdue">Hết hạn</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500">Đang tải bài tập...</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">Chưa có bài tập nào phù hợp.</div>
      ) : (
        <div className="space-y-3 p-4 md:p-5">
          {filtered.map((asg) => {
            const over = isOver(asg.deadline);
            const targetCount = Array.isArray(asg.assignedTo) && asg.assignedTo.length ? asg.assignedTo.length : students.length;
            const expanded = expandedId === asg.id;

            return (
              <div key={asg.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          asg.published
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                      >
                        {asg.published ? "Published" : "Draft"}
                      </span>
                      {over && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                          <FiAlertTriangle /> Hết hạn
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        <FiUsers /> {targetCount || 0} học viên
                      </span>
                    </div>

                    <h4 className="mt-2 text-lg font-bold text-slate-900 line-clamp-2">
                      {displayText(asg.title, "Untitled assignment")}
                    </h4>

                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <FiCalendar /> Giao: {fmt(asg.startAt)} · Hạn: {fmt(asg.deadline)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <FiBarChart2 /> Test ID: <span className="font-mono">{asg.testId || "—"}</span>
                      </span>
                    </div>

                    {asg.note && <p className="mt-2 line-clamp-2 text-sm text-slate-500">Ghi chú: {displayText(asg.note)}</p>}
                  </div>

                  <div className="flex flex-wrap xl:flex-col gap-2 xl:w-40 shrink-0">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : asg.id)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      {expanded ? <FiChevronUp /> : <FiChevronDown />}
                      Tiến độ
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(asg)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                    >
                      <FiEdit3 /> Sửa
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(asg)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                    >
                      <FiTrash2 /> Xoá
                    </button>
                  </div>
                </div>

                {expanded && (
                  <div className="mt-4">
                    <AssignmentProgress assignment={asg} classInfo={classInfo} students={students} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
